<script lang="ts">
  import { ChevronLeft, ChevronRight } from 'lucide-svelte';

  interface Props {
    page?: number;
    pageCount: number;
    total?: number;
    pageSize?: number;
    itemLabel?: string;
    label?: string;
    /** While a page loads: every button is aria-disabled (so focus stays put) and clicks are ignored. */
    busy?: boolean;
    onChange?: (page: number) => void;
  }

  let {
    page = $bindable(),
    pageCount,
    total,
    pageSize,
    itemLabel = 'results',
    label = 'Pagination',
    busy = false,
    onChange,
  }: Props = $props();

  const number = new Intl.NumberFormat('en-AU');
  const current = $derived(Math.min(Math.max(1, page ?? 1), Math.max(1, pageCount)));

  const pages = $derived.by(() => {
    if (pageCount <= 7) return Array.from({ length: pageCount }, (_, index) => index + 1);
    const wanted = new Set([1, pageCount, current - 1, current, current + 1]);
    if (current <= 3) [2, 3, 4].forEach((value) => wanted.add(value));
    if (current >= pageCount - 2) [pageCount - 3, pageCount - 2, pageCount - 1].forEach((value) => wanted.add(value));
    const sorted = [...wanted].filter((value) => value >= 1 && value <= pageCount).sort((a, b) => a - b);
    const result: (number | 'gap')[] = [];
    for (const value of sorted) {
      const previous = result[result.length - 1];
      if (typeof previous === 'number' && value - previous === 2) result.push(previous + 1);
      else if (typeof previous === 'number' && value - previous > 2) result.push('gap');
      result.push(value);
    }
    return result;
  });

  const summary = $derived.by(() => {
    if (total !== undefined && pageSize) {
      if (total === 0) return `No ${itemLabel}`;
      const first = (current - 1) * pageSize + 1;
      const last = Math.min(current * pageSize, total);
      return `Showing ${number.format(first)}–${number.format(last)} of ${number.format(total)} ${itemLabel}`;
    }
    return `Page ${number.format(current)} of ${number.format(Math.max(1, pageCount))}`;
  });

  function go(next: number) {
    if (busy || next < 1 || next > pageCount || next === current) return;
    page = next;
    onChange?.(next);
  }
</script>

<div class="ws-pagination" class:ws-pagination--busy={busy}>
  <p class="ws-pagination__summary" aria-live="polite">{summary}</p>
  {#if pageCount > 1}
    <nav aria-label={label}>
      <ul class="ws-pagination__controls">
        <li>
          <!-- aria-disabled, not disabled: the step a keyboard user just pressed keeps focus at either end. -->
          <button
            type="button"
            class="ws-pagination__page ws-pagination__step"
            aria-disabled={busy || current <= 1 ? 'true' : undefined}
            onclick={() => go(current - 1)}
          >
            <ChevronLeft size={18} aria-hidden="true" />
            <span>Previous</span>
          </button>
        </li>
        <!-- Keyed by page number, so the button a keyboard user pressed survives the list re-rendering around it. -->
        {#each pages as entry, index (typeof entry === 'number' ? entry : `gap-${index}`)}
          <li class="ws-pagination__number">
            {#if entry === 'gap'}
              <span class="ws-pagination__gap" aria-hidden="true">…</span>
            {:else}
              <button
                type="button"
                class="ws-pagination__page"
                aria-label="Page {entry}"
                aria-current={entry === current ? 'page' : undefined}
                aria-disabled={busy ? 'true' : undefined}
                onclick={() => go(entry)}
              >
                {entry}
              </button>
            {/if}
          </li>
        {/each}
        <li>
          <button
            type="button"
            class="ws-pagination__page ws-pagination__step"
            aria-disabled={busy || current >= pageCount ? 'true' : undefined}
            onclick={() => go(current + 1)}
          >
            <span>Next</span>
            <ChevronRight size={18} aria-hidden="true" />
          </button>
        </li>
      </ul>
    </nav>
  {/if}
</div>
